import { getQuickloadUrls } from './getQuickloadUrls';
import { makeUrl } from './makeUrl';
import type { Callback, Preload } from './quickload.types';
import { preloadsHash } from './preloadsHash';
import { cleanPreload } from './cleanPreload';
import { getTraceId } from './getTraceId';

import { TrelloWindow } from '@trello/window-types';
declare const window: TrelloWindow;

const parseJSON = function (data: string) {
  try {
    return JSON.parse(data);
  } catch (e) {
    return null;
  }
};

const ajaxGetJSON = function (
  url: string,
  next: Callback,
  operationName = 'unknown',
  operationSource = 'model-loader',
  traceId?: string,
  taskName?: string,
) {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', url, true);
  xhr.setRequestHeader('Accept', 'application/json,text/plain');
  xhr.setRequestHeader(
    'X-Trello-Client-Version',
    window.trelloVersion || 'dev-0',
  );
  xhr.setRequestHeader('X-Trello-Operation-Source', operationSource);
  xhr.setRequestHeader('X-Trello-Operation-Name', operationName);

  if (traceId) {
    xhr.setRequestHeader('X-Trello-TraceId', traceId);
    xhr.setRequestHeader('X-Trello-Task', taskName || 'not-implemented');
  }

  xhr.onreadystatechange = function () {
    if (xhr.readyState === 4) {
      if (xhr.status !== 200) {
        next([xhr.status, xhr.responseText]);
      } else {
        next(null, [parseJSON(xhr.responseText), xhr]);
      }
    }
  };

  xhr.send(null);
};

const removePreload = function (url: string) {
  if (!(url in preloadsHash)) {
    return;
  }
  delete preloadsHash[url];
};

const preload = function (
  url: string,
  operationName = 'preload',
  traceId?: string,
) {
  if (!url) {
    return;
  }

  const preloadObject: Preload = {
    isLoading: true,
    callbacks: [],
    start: Date.now(),
    used: false,
    url,
    traceId,
  };
  preloadsHash[url] = preloadObject;

  ajaxGetJSON(
    url,
    function (err, data) {
      let callback;
      preloadObject.isLoading = false;
      if (err) {
        preloadObject.error = err;
        for (callback of Array.from(preloadObject.callbacks)) {
          callback(err);
        }
        return;
      }
      preloadObject.data = data;
      for (callback of Array.from(preloadObject.callbacks)) {
        callback(null, data);
      }
      // If the request isn't made within 10 seconds, it'll have to be made again
      setTimeout(() => removePreload(url), 10000);
    },
    operationName,
    'quickload',
    traceId,
  );
};

const quickload = {
  // The "init" method is called once from index.ejs to kick off preloading
  init() {
    const traceId = getTraceId();
    const preloadUrls = getQuickloadUrls();
    for (const [url, operationName] of preloadUrls) {
      preload(url, operationName, traceId);
    }
  },
  getQuickloadUrls,
  makeUrl,
  load(
    url: string,
    next: Callback = () => {},
    operationName: string,
    operationSource: string,
    traceId?: string,
    taskName?: string,
  ) {
    const preloadObject = preloadsHash[url];
    if (preloadObject !== undefined) {
      preloadObject.used = true;
      if (preloadObject.isLoading) {
        preloadObject.callbacks.push(next);
      } else {
        next(preloadObject.error, preloadObject.data);
        removePreload(url);
      }
      return cleanPreload(preloadObject);
    } else {
      ajaxGetJSON(url, next, operationName, operationSource, traceId, taskName);
    }
  },
  getPreloadTraceId(): string | undefined {
    const preloadUrls = getQuickloadUrls();
    for (const [url] of preloadUrls) {
      if (url in preloadsHash) {
        return preloadsHash[url].traceId;
      }
    }
    return;
  },
  clear() {
    for (const url in preloadsHash) {
      removePreload(url);
    }
  },
};

// eslint-disable-next-line @trello/no-module-logic
quickload.init();
export const QuickLoad = (window.QuickLoad = quickload);
